import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronDown, User, LogOut, Heart, Settings } from 'lucide-react';
import { categories, deals, stores } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import ProfileModal from './ProfileModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ deals: [], stores: [], categories: [] });
  const [mobileSearchResults, setMobileSearchResults] = useState({ deals: [], stores: [], categories: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showMobileSearchResults, setShowMobileSearchResults] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, loading } = useAuth();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Categories', path: '/categories', hasDropdown: true },
    { name: 'Stores', path: '/stores' },
    { name: 'Contact', path: '/contact' },
    { name: 'Blog', path: '/blog' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCategoriesOpen(false);
    setMobileSearchQuery('');
    setShowMobileSearchResults(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchQuery.trim().length > 1) {
      const query = searchQuery.toLowerCase();
      const matchedDeals = deals.filter(deal => 
        deal.title.toLowerCase().includes(query) ||
        deal.description?.toLowerCase().includes(query)
      ).slice(0, 5);
      const matchedStores = stores.filter(store =>
        store.name.toLowerCase().includes(query)
      ).slice(0, 3);
      const matchedCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(query)
      ).slice(0, 3);
      setSearchResults({ deals: matchedDeals, stores: matchedStores, categories: matchedCategories });
      setShowSearchResults(true);
    } else {
      setSearchResults({ deals: [], stores: [], categories: [] });
      setShowSearchResults(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (mobileSearchQuery.trim().length > 1) {
      const query = mobileSearchQuery.toLowerCase();
      const matchedDeals = deals.filter(deal => 
        deal.title.toLowerCase().includes(query) ||
        deal.description?.toLowerCase().includes(query)
      ).slice(0, 4);
      const matchedStores = stores.filter(store =>
        store.name.toLowerCase().includes(query)
      ).slice(0, 3);
      const matchedCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(query)
      ).slice(0, 3);
      setMobileSearchResults({ deals: matchedDeals, stores: matchedStores, categories: matchedCategories });
      setShowMobileSearchResults(true);
    } else {
      setMobileSearchResults({ deals: [], stores: [], categories: [] });
      setShowMobileSearchResults(false);
    }
  }, [mobileSearchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/stores?search=${encodeURIComponent(searchQuery)}`);
      setShowSearchResults(false);
      setSearchQuery('');
    }
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      navigate(`/stores?search=${encodeURIComponent(mobileSearchQuery)}`);
      setMobileMenuOpen(false);
      setMobileSearchQuery('');
      setShowMobileSearchResults(false);
    }
  };

  const handleMobileResultClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setMobileSearchQuery('');
    setShowMobileSearchResults(false);
  };

  const handleOpenAuth = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    setMobileMenuOpen(false);
  };

  const hasResults = searchResults.deals?.length > 0 || 
                     searchResults.stores?.length > 0 || 
                     searchResults.categories?.length > 0;

  const hasMobileResults = mobileSearchResults.deals?.length > 0 || 
                           mobileSearchResults.stores?.length > 0 || 
                           mobileSearchResults.categories?.length > 0;

  return (
    <>
      <header className="bg-white sticky top-0 z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center h-20">
            {/* Mobile Menu Button */}
            <button
              className="xl:hidden text-slate-700 p-2 hover:bg-slate-100 rounded-xl transition-colors mr-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>

            {/* Logo - Desktop */}
            <Link to="/" className="hidden sm:flex items-center gap-2 flex-shrink-0 group">
              <img src="/logo.png" alt="Damart Clothing Coupons" className="w-10 h-10 rounded-lg group-hover:scale-105 transition-transform" />
              <div>
                <span className="font-heading font-bold text-xl text-slate-900">Damart Clothing</span>
                <span className="font-heading font-bold text-xl text-slate-700"> Coupons</span>
              </div>
            </Link>

            {/* Logo - Mobile (Centered) */}
            <div className="flex-1 flex justify-center sm:hidden">
              <Link to="/" className="flex items-center gap-2 group">
                <img src="/logo.png" alt="Damart Clothing Coupons" className="w-9 h-9 rounded-lg group-hover:scale-105 transition-transform" />
                <div className="flex flex-col leading-tight">
                  <span className="font-heading font-bold text-sm text-slate-900">Damart Clothing</span>
                  <span className="font-heading font-bold text-xs text-slate-700">Coupons</span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center justify-center flex-1 mx-8">
              <div className="flex items-center gap-1">
                {navItems.map((item) => (
                  <div key={item.name} className="relative group">
                    {item.hasDropdown ? (
                      <button
                        className={`flex items-center px-4 py-2 font-medium text-sm rounded-full transition-all ${
                          isActive(item.path) 
                            ? 'bg-slate-100 text-slate-900' 
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                        onMouseEnter={() => setCategoriesOpen(true)}
                        onMouseLeave={() => setCategoriesOpen(false)}
                      >
                        {item.name}
                        <ChevronDown size={16} className="ml-1" />
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`px-4 py-2 font-medium text-sm rounded-full transition-all block ${
                          isActive(item.path) 
                            ? 'bg-slate-100 text-slate-900' 
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}

                    {/* Categories Dropdown */}
                    {item.hasDropdown && (
                      <div
                        className={`absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-xl py-3 w-56 transition-all duration-200 border border-slate-100 ${
                          categoriesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                        }`}
                        onMouseEnter={() => setCategoriesOpen(true)}
                        onMouseLeave={() => setCategoriesOpen(false)}
                      >
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`}
                            className="block px-4 py-2.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 text-sm transition-colors mx-2 rounded-lg"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Link
                  to="/privacy-policy"
                  className="px-4 py-2 text-slate-600 font-medium text-sm hover:text-slate-900 hover:bg-slate-100 rounded-full transition-all"
                >
                  Privacy
                </Link>
              </div>
            </nav>

            {/* Search and Auth */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Desktop Search */}
              <div className="hidden md:block relative">
                <form onSubmit={handleSearchSubmit} className="flex items-center">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search deals..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => searchQuery.length > 1 && setShowSearchResults(true)}
                      onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                      className="px-4 py-2.5 pl-10 text-sm w-44 lg:w-56 bg-slate-100 rounded-full focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all"
                      data-testid="desktop-search-input"
                    />
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                </form>

                {/* Search Results */}
                {showSearchResults && hasResults && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 max-h-96 overflow-y-auto animate-fadeIn">
                    {searchResults.categories?.length > 0 && (
                      <div className="p-3 border-b border-slate-100">
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider px-2 mb-2">Categories</p>
                        {searchResults.categories.map(cat => (
                          <Link
                            key={cat.id}
                            to={cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`}
                            className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900 rounded-lg transition-colors"
                            onClick={() => setShowSearchResults(false)}
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    )}
                    {searchResults.stores?.length > 0 && (
                      <div className="p-3 border-b border-slate-100">
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider px-2 mb-2">Stores</p>
                        {searchResults.stores.map(store => (
                          <Link
                            key={store.id}
                            to={`/store/${store.slug}`}
                            className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900 rounded-lg transition-colors"
                            onClick={() => setShowSearchResults(false)}
                          >
                            {store.name}
                          </Link>
                        ))}
                      </div>
                    )}
                    {searchResults.deals?.length > 0 && (
                      <div className="p-3">
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider px-2 mb-2">Deals</p>
                        {searchResults.deals.map(deal => (
                          <Link
                            key={deal.id}
                            to={`/store/${deal.store}`}
                            className="flex items-center gap-3 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            onClick={() => setShowSearchResults(false)}
                          >
                            <img src={deal.image} alt="" className="w-10 h-10 object-cover rounded-xl" />
                            <div className="flex-1 min-w-0">
                              <p className="truncate font-medium">{deal.title}</p>
                              <p className="text-slate-900 font-bold">${deal.salePrice?.toFixed(2)}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile Search */}
              <button 
                className="md:hidden text-slate-600 p-2 hover:bg-slate-100 rounded-xl transition-colors"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Search size={22} />
              </button>

              {/* Desktop Auth */}
              {!loading && (
                <div className="hidden md:flex items-center gap-2">
                  {isAuthenticated ? (
                    <div className="relative">
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 rounded-full transition-colors"
                        data-testid="user-menu-btn"
                      >
                        <div className="w-9 h-9 bg-gradient-to-br from-slate-900 to-slate-700 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {user?.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-slate-700 hidden lg:inline">{user?.name?.split(' ')[0]}</span>
                        <ChevronDown size={16} className={`text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                      </button>

                      {showUserMenu && (
                        <div 
                          className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 py-2 animate-fadeIn"
                          onMouseLeave={() => setShowUserMenu(false)}
                        >
                          <div className="px-4 py-3 border-b border-slate-100">
                            <p className="font-semibold text-slate-800 truncate">{user?.name}</p>
                            <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                          </div>
                          <Link
                            to="/favorites"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-100 transition-colors"
                            data-testid="favorites-link"
                          >
                            <Heart size={18} className="text-slate-900" />
                            My Favorites
                            {user?.favorites?.length > 0 && (
                              <span className="ml-auto bg-slate-100 text-slate-900 text-xs font-bold px-2 py-0.5 rounded-full">
                                {user.favorites.length}
                              </span>
                            )}
                          </Link>
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              setShowProfileModal(true);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-100 transition-colors"
                            data-testid="profile-settings-btn"
                          >
                            <Settings size={18} className="text-slate-900" />
                            Settings
                          </button>
                          <div className="border-t border-slate-100 my-1"></div>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors"
                            data-testid="logout-btn"
                          >
                            <LogOut size={18} />
                            Sign Out
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleOpenAuth('login')}
                        className="text-slate-700 text-sm font-semibold px-4 py-2.5 hover:bg-slate-100 rounded-full transition-colors"
                        data-testid="login-btn"
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => handleOpenAuth('register')}
                        className="bg-gradient-to-r from-slate-900 to-slate-700 text-white text-sm font-bold px-6 py-2.5 rounded-full hover:shadow-lg hover:scale-105 transition-all btn-press"
                        data-testid="register-btn"
                      >
                        Get Started
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 xl:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Drawer */}
      <div 
        className={`fixed top-0 left-0 h-full w-80 bg-white z-50 transform transition-transform duration-300 ease-out xl:hidden flex flex-col shadow-2xl ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Drawer Header */}
        <div className="p-5 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Lyli Scott Coupons Store" className="w-9 h-9 rounded-lg" />
            <div className="flex flex-col leading-tight">
              <span className="font-heading font-bold text-base text-slate-900">Lyli Scott</span>
              <span className="font-heading font-semibold text-xs text-slate-700">Coupons Store</span>
            </div>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="text-slate-500 p-2 hover:bg-slate-100 rounded-xl transition-colors"
            data-testid="close-menu-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Mobile User Section */}
        {!loading && (
          <div className="p-5 border-b border-slate-100 bg-slate-50">
            {isAuthenticated ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-900 to-slate-700 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{user?.name}</p>
                    <p className="text-sm text-slate-500 truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link
                    to="/favorites"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 text-slate-900 border-2 border-slate-300 rounded-xl text-sm font-semibold hover:bg-slate-100 transition-colors"
                    data-testid="mobile-favorites-link"
                  >
                    <Heart size={16} />
                    Favorites
                    {user?.favorites?.length > 0 && (
                      <span className="bg-slate-900 text-white text-xs px-1.5 rounded-full">
                        {user.favorites.length}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setShowProfileModal(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
                    data-testid="mobile-settings-btn"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-red-600 border-2 border-red-200 rounded-xl text-sm font-semibold hover:bg-red-50 transition-colors"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => handleOpenAuth('login')}
                  className="flex-1 py-3 text-slate-900 border-2 border-slate-300 rounded-xl font-semibold text-sm hover:bg-slate-100 transition-colors"
                  data-testid="mobile-login-btn"
                >
                  Sign In
                </button>
                <button
                  onClick={() => handleOpenAuth('register')}
                  className="flex-1 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all"
                  data-testid="mobile-register-btn"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="p-5 border-b border-slate-100 relative">
          <form onSubmit={handleMobileSearchSubmit}>
            <div className="relative">
              <input
                type="text"
                placeholder="Search deals, stores..."
                value={mobileSearchQuery}
                onChange={(e) => setMobileSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-11 bg-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all"
                data-testid="mobile-search-input"
              />
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
          </form>

          {showMobileSearchResults && hasMobileResults && (
            <div className="absolute left-5 right-5 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-100 z-50 max-h-64 overflow-y-auto animate-fadeIn">
              {mobileSearchResults.categories?.length > 0 && (
                <div className="p-2 border-b border-slate-100">
                  <p className="text-xs text-slate-400 font-semibold uppercase px-2 mb-1">Categories</p>
                  {mobileSearchResults.categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => handleMobileResultClick(cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`)}
                      className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
              {mobileSearchResults.stores?.length > 0 && (
                <div className="p-2 border-b border-slate-100">
                  <p className="text-xs text-slate-400 font-semibold uppercase px-2 mb-1">Stores</p>
                  {mobileSearchResults.stores.map(store => (
                    <button
                      key={store.id}
                      onClick={() => handleMobileResultClick(`/store/${store.slug}`)}
                      className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      {store.name}
                    </button>
                  ))}
                </div>
              )}
              {mobileSearchResults.deals?.length > 0 && (
                <div className="p-2">
                  <p className="text-xs text-slate-400 font-semibold uppercase px-2 mb-1">Deals</p>
                  {mobileSearchResults.deals.map(deal => (
                    <button
                      key={deal.id}
                      onClick={() => handleMobileResultClick(`/store/${deal.store}`)}
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <img src={deal.image} alt="" className="w-8 h-8 object-cover rounded-lg" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-xs font-medium">{deal.title}</p>
                        <p className="text-slate-900 font-bold text-xs">${deal.salePrice?.toFixed(2)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="overflow-y-auto flex-1 py-2">
          {navItems.map((item) => (
            <div key={item.name}>
              {item.hasDropdown ? (
                <>
                  <button
                    onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                    className="w-full flex items-center justify-between px-5 py-3.5 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                  >
                    <span>{item.name}</span>
                    <ChevronDown 
                      size={20} 
                      className={`text-slate-400 transition-transform duration-200 ${mobileCategoriesOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  <div className={`bg-slate-50 overflow-hidden transition-all duration-300 ${
                    mobileCategoriesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`}
                        className="block px-8 py-3 text-slate-600 text-sm hover:bg-slate-100 hover:text-slate-900 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`block px-5 py-3.5 font-medium transition-colors ${
                    isActive(item.path) 
                      ? 'text-slate-900 bg-slate-100' 
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
          
          <Link
            to="/privacy-policy"
            className="block px-5 py-3.5 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            Privacy Policy
          </Link>
        </nav>

        {/* App Info */}
        <div className="border-t border-slate-100 bg-slate-50 p-5">
          <div className="text-center space-y-1">
            <p className="text-sm font-semibold text-slate-700">Damart Clothing Coupons</p>
            <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
              <span>v1.0.0</span>
              <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span>Build 2026.01.24</span>
            </div>
          </div>
        </div>
      </div>

      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      <ProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </>
  );
};

export default Header;
